import { ref, onUnmounted } from 'vue'
import { useGoogleMapsLoader } from './useGoogleMapsLoader.js'
import { useAddressExtraction } from './useAddressExtraction.js'

/**
 * Composable pour utiliser Google Places Autocomplete
 * Limite: 80 lignes max
 * 
 * @param {Object} options - Options de configuration
 * @param {Function} options.onPlaceSelected - Callback appelé quand une adresse est sélectionnée
 * @param {Array} options.types - Types de résultats (default: ['address'])
 * @param {String} options.componentRestrictions - Restriction par pays (default: 'fr')
 */
export function useGooglePlaces(options = {}) {
  const inputRef = ref(null)
  const autocomplete = ref(null)
  const isLoaded = ref(false)
  const error = ref(null)

  const {
    onPlaceSelected = () => {},
    types = ['address'],
    componentRestrictions = { country: 'fr' }
  } = options

  const { loadGoogleMapsScript } = useGoogleMapsLoader()
  const { extractAddressComponents } = useAddressExtraction()

  const initAutocomplete = async (inputElement) => {
    if (!inputElement) return

    inputRef.value = inputElement

    try {
      await loadGoogleMapsScript()
      isLoaded.value = true

      autocomplete.value = new window.google.maps.places.Autocomplete(inputElement, {
        types,
        componentRestrictions,
        fields: ['address_components', 'formatted_address', 'geometry', 'name']
      })

      autocomplete.value.addListener('place_changed', () => {
        const place = autocomplete.value.getPlace()

        if (!place.address_components) {
          return
        }

        const addressData = extractAddressComponents(place)
        onPlaceSelected(addressData)
      })
    } catch (err) {
      error.value = err.message
      console.error('Erreur Google Places:', err)
    }
  }

  onUnmounted(() => {
    if (autocomplete.value) {
      window.google?.maps?.event?.clearInstanceListeners(autocomplete.value)
    }
  })

  return {
    initAutocomplete,
    isLoaded,
    error,
    inputRef
  }
}
